import React, { useEffect, useMemo, useState } from "react";
import ContentArea from "../ContentArea/ContentArea";
import Layout from "./Layout/Layout";
import StatusMessage from "../StatusMessage/StatusMessage";
import StatusDataLoader from "../../services/StatusDataLoader";
import {
  HalvingEraRow,
  buildHalvingSchedule,
} from "../../utils/Calculators/HalvingCalculator";
import { useSeo } from "../../utils/Hooks/useSeo";
import "./HalvingPage.css";

const SEO_DESCRIPTION =
  "Browse past and future Bitcoin halving eras with reward schedule, supply progression, and estimated upcoming milestones.";

const formatBlock = (value: number) =>
  value.toLocaleString("en-US", { maximumFractionDigits: 0 });

const formatDate = (date: Date) =>
  new Intl.DateTimeFormat("en-US", {
    year: "numeric",
    month: "short",
    day: "2-digit",
  }).format(date);

const formatBtc = (value: number, maximumFractionDigits = 8) =>
  `${value.toLocaleString("en-US", { maximumFractionDigits })} BTC`;

const getOrdinal = (value: number) => {
  const mod10 = value % 10;
  const mod100 = value % 100;

  if (mod10 === 1 && mod100 !== 11) return `${value}st`;
  if (mod10 === 2 && mod100 !== 12) return `${value}nd`;
  if (mod10 === 3 && mod100 !== 13) return `${value}rd`;
  return `${value}th`;
};

const getStatusClass = (status: HalvingEraRow["status"]) => {
  if (status === "Current") {
    return "halving-status current";
  }
  if (status === "Completed") {
    return "halving-status completed";
  }
  return "halving-status upcoming";
};


const getTimeToHalvingLabel = (row: HalvingEraRow) => {
  if (row.status === "Completed") {
    return "Completed";
  }

  return `${formatBlock(row.blocksUntilHalving)} blocks (~${row.estimatedYearsUntilHalving.toFixed(2)}y)`;
};

export const HalvingPage = () => {
  const [loading, setLoading] = useState(true);
  const [currentBlock, setCurrentBlock] = useState(0);

  useSeo({
    title: "Bitcoin Halving Schedule",
    description: SEO_DESCRIPTION,
    canonicalPath: "/halving",
  });

  useEffect(() => {
    const load = async () => {
      const status = await StatusDataLoader.getStatusData();
      setCurrentBlock(status?.network?.currentBlock ?? 0);
      setLoading(false);
    };

    load();
  }, []);

  const schedule = useMemo(
    () => buildHalvingSchedule({ currentBlock, projectedEpochsAhead: 10 }),
    [currentBlock],
  );

  const currentEra = useMemo(
    () => schedule.rows.find((row) => row.status === "Current"),
    [schedule.rows],
  );

  const nextEra = useMemo(
    () => schedule.rows.find((row) => row.status === "Upcoming"),
    [schedule.rows],
  );

  const renderContent = () => {
    if (loading) {
      return (
        <div className="progress-circle">
          <progress className="pure-material-progress-circular" />
        </div>
      );
    }

    if (!schedule.rows.length || !currentEra) {
      return (
        <StatusMessage
          title="We could not build the halving schedule."
          description="Please try again in a minute."
          minHeightRem={24}
        />
      );
    }

    return (
      <div className="content-lines">
        <section className="halving-overview-card">
          <div className="halving-overview-top">
            <div>
              <h1 className="halving-overview-title">Bitcoin Halving Schedule</h1>
              <div className="halving-overview-subtitle">
                {SEO_DESCRIPTION}
              </div>
            </div>
            <div className="halving-current-panel">
              <div className="halving-current-label">Current Epoch</div>
              <div className="halving-current-value">{getOrdinal(currentEra.epoch)}</div>
            </div>
          </div>

          <div className="halving-metrics-grid">
            <div className="halving-metric">
              <div className="halving-metric-label">Current block</div>
              <div className="halving-metric-value">{formatBlock(currentBlock)}</div>
            </div>
            <div className="halving-metric">
              <div className="halving-metric-label">Current subsidy</div>
              <div className="halving-metric-value">{formatBtc(currentEra.rewardBtcPerBlock)}</div>
            </div>
            <div className="halving-metric">
              <div className="halving-metric-label">Blocks to next halving</div>
              <div className="halving-metric-value">{formatBlock(currentEra.blocksUntilHalving)}</div>
            </div>
            <div className="halving-metric">
              <div className="halving-metric-label">Estimated next halving</div>
              <div className="halving-metric-value">{formatDate(currentEra.halvingDate)}</div>
            </div>
          </div>
        </section>

        <section className="halving-table-card">
          <div className="halving-table-header">
            <h2>Past + Future Halvings</h2>
            <p>
              Dates marked <strong>Actual</strong> are historical on-chain dates.{" "}
              <strong>Estimated</strong> dates use current pace from block{" "}
              {formatBlock(currentBlock)}.
            </p>
          </div>

          <div className="halving-table-wrap">
            <table className="halving-table">
              <thead>
                <tr>
                  <th>Epoch</th>
                  <th>Block Range</th>
                  <th>Reward / Block</th>
                  <th>BTC Issued in Epoch</th>
                  <th>Cumulative BTC</th>
                  <th>Start</th>
                  <th>Halving</th>
                  <th>Time to Halving</th>
                </tr>
              </thead>
              <tbody>
                {schedule.rows.map((row) => (
                  <tr
                    key={row.epoch}
                    className={row.status === "Current" ? "halving-row-current" : ""}
                  >
                    <td>
                      <div className="halving-epoch-cell">
                        <span className={getStatusClass(row.status)}>
                          {getOrdinal(row.epoch)}
                        </span>
                      </div>
                    </td>
                    <td>
                      {formatBlock(row.startBlock)} - {formatBlock(row.halvingBlock)}
                    </td>
                    <td>{formatBtc(row.rewardBtcPerBlock)}</td>
                    <td>{formatBtc(row.btcIssuedInEpoch, 4)}</td>
                    <td>{formatBtc(row.cumulativeSupplyBtc, 2)}</td>
                    <td>
                      <div className="halving-date-main">{formatDate(row.startDate)}</div>
                      <div className="halving-date-meta">
                        {row.startDateBasis} {row.startDate.getUTCFullYear()}
                      </div>
                    </td>
                    <td>
                      <div className="halving-date-main">{formatDate(row.halvingDate)}</div>
                      <div className="halving-date-meta">
                        {row.halvingDateBasis} {row.halvingDate.getUTCFullYear()}
                      </div>
                    </td>
                    <td>{getTimeToHalvingLabel(row)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {nextEra && (
            <div className="halving-footnote">
              Next era starts at block {formatBlock(nextEra.startBlock)} around{" "}
              {formatDate(nextEra.startDate)}.
            </div>
          )}
        </section>
      </div>
    );
  };

  return (
    <Layout>
      <ContentArea isTopArea>
        {renderContent()}
      </ContentArea>
    </Layout>
  );
};
